import React from 'react';

import { portalApi } from '../../../api';
import Toast from '../../../components/Toast';
import pinsTheme from '../../../styles/theme';
import { toast } from '../../../utils/toastUtil';
import { hideModal } from '../modal';
/**
 * Let This file serve as a model for all future redux modules. Each module should have an index, actions, and
 * selectors files. In the index is where the reducer for the module is defined, and actions and selectors files
 * service only to export the actions and selectors from the index. See below for a basic example.
 */
export const APP_MOUNTED = 'APP_MOUNTED';

/**
 * Why is appMounted a curried function you ask? Why do we need the extra "() =>" arrow function in
 * front of the dispatch? This is because often times we want to pass data from the current component
 * into the action creator, like so: dispatch(someAction(someData)) and without the curry we cannot do so.
 */
export const appMounted = () => dispatch => dispatch({ type: APP_MOUNTED });

/**
 * Action type constants live directly next to the action creator method they support
 */
const APP_SET_CURRENT_PAGE = 'APP_SET_CURRENT_PAGE';

export const setCurrentPage = page => (dispatch, getState) => {
  if (getState().app?.currentPage === page) return;
  if (getState().modal?.isOpen) dispatch(hideModal());
  dispatch({ type: APP_SET_CURRENT_PAGE, page });

  const appTitle = 'PINS Advantage';
  const capitalizePage = page.charAt(0).toUpperCase() + page.slice(1);

  document.title = page === 'list-departments' ? appTitle : `${appTitle} – ${capitalizePage}`;
};

export const createErrorToast = ({
  title,
  description,
  duration = 9000,
  position = 'bottom',
  size = 'lg',
  isClosable = false,
  error = null,
  closeAllToasts = true
}) => dispatch => {
  if (error && (error?.status === 403 || error?.message === 'Unauthorized')) {
    // unauthorized toast already handled in baseApi
    return;
  }

  if (closeAllToasts) {
    toast.closeAll();
  }

  toast({
    duration,
    position,
    render: props => (
      <Toast
        description={description}
        isClosable={isClosable}
        size={size}
        status="error"
        title={title}
        {...props}
      />
    )
  });
};

const CREATE_SUCCESS_TOAST = 'CREATE_SUCCESS_TOAST';
export const createSuccessToast = ({
  title,
  description,
  duration = 9000,
  position = 'bottom',
  size = 'lg'
}) => dispatch => {
  toast.closeAll();

  toast({
    duration,
    position,
    render: props => (
      <Toast
        isClosable
        description={description}
        size={size}
        status="success"
        title={title}
        {...props}
      />
    )
  });
};

const CREATE_TOAST = 'CREATE_TOAST';
export const createToast = ({
  title,
  description,
  duration = 9000,
  position = 'bottom',
  status = 'general',
  size = 'lg'
}) => dispatch => {
  toast.closeAll();

  toast({
    duration,
    position,
    render: props => (
      <Toast
        isClosable
        description={description}
        size={size}
        status={status}
        title={title}
        {...props}
      />
    )
  });
};

/**
 * DEFAULT_STATE is always the beginning state for this reducer. Its the schema for this module
 * in the store, if you will.
 */
const DEFAULT_STATE = {
  mounted: false,
  ready: false,
  currentPage: null
};

/**
 * Here is the reducer. The brain of the module. This is where actions come in (with or without data) and
 * the altered state comes out. Make sure to always create a new object and never modify the incomming state.
 * The entire basis of redux rests upon the devlopers dilligence to obey immutability because it does object
 * comparisons to detect state changes.
 */
const appReducer = (state = DEFAULT_STATE, action) => {
  switch (action.type) {
    case APP_MOUNTED:
      return {
        ...state,
        mounted: true
      };
    case APP_SET_CURRENT_PAGE:
      return { ...state, currentPage: action.page };
    default:
      return state;
  }
};

/**
 * Selectors are how you can get data out of the store easily, in a structured, named manner. They're mostly just
 * utility functions but they behaves nicely with the useSelector react hook.
 */
export const selectIsAppMounted = state => state.app.mounted;
export const selectIsAppReady = state => state.app.ready;
export const selectEmailTemplateDictionary = state => state.app.dictionary;
export const selectCurrentPage = state => state.app.currentPage;

export default appReducer;
